/**
 * useEvents Hook
 *
 * React hook for tracking events in components.
 * Provides convenient methods for common event types.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  trackEvent,
  trackPageView,
  trackMoodCheckIn,
  trackTechniqueSession,
  trackModuleCompleted,
  trackCrisisDetected,
  trackAICoachMessage,
  type EventInput,
} from '../lib/events';

interface UseEventsOptions {
  /** Track page view on mount */
  trackPageViewOnMount?: boolean;
  /** Custom page path (defaults to window.location.pathname) */
  pagePath?: string;
  /** Custom page title (defaults to document.title) */
  pageTitle?: string;
}

interface UseEventsReturn {
  /** Track a generic event */
  track: (input: EventInput) => Promise<boolean>;

  /** Track page view */
  trackPage: (path?: string, title?: string) => Promise<boolean>;

  /** Track mood check-in */
  trackMood: (
    mood: 'stressed' | 'okay' | 'good',
    context?: string
  ) => Promise<boolean>;

  /** Track technique session */
  trackTechnique: (
    techniqueId: string,
    techniqueName: string,
    durationMs: number,
    stressBefore?: number,
    stressAfter?: number
  ) => Promise<boolean>;

  /** Track module completion */
  trackModule: (
    moduleId: string,
    moduleName: string,
    category?: string
  ) => Promise<boolean>;

  /** Track crisis detection */
  trackCrisis: (
    method: 'deterministic' | 'ai_secondary',
    severity: 'low' | 'medium' | 'high',
    source: 'chat' | 'journal' | 'reflection' | 'feedback' | 'other'
  ) => Promise<boolean>;

  /** Track AI coach message */
  trackAIMessage: (
    conversationId: string,
    messageIndex: number,
    direction: 'sent' | 'received'
  ) => Promise<boolean>;

  /** Track onboarding step */
  trackOnboardingStep: (
    stepId: string,
    stepName: string,
    stepIndex: number
  ) => Promise<boolean>;

  /** Track onboarding completion */
  trackOnboardingComplete: (
    userIntent?: string,
    gradeLevel?: string,
    totalTimeMs?: number
  ) => Promise<boolean>;

  /** Track goal creation */
  trackGoalCreated: (
    goalId: string,
    goalType: 'academic' | 'skill' | 'wellness' | 'social' | 'personal',
    milestoneCount?: number
  ) => Promise<boolean>;

  /** Track goal completion */
  trackGoalCompleted: (goalId: string) => Promise<boolean>;

  /** Track journal entry */
  trackJournalEntry: (
    entryType: 'personal' | 'reflection' | 'vent',
    wordCount: number,
    hasPrompt?: boolean
  ) => Promise<boolean>;

  /** Track EF game completion */
  trackEFGame: (
    gameId: string,
    gameName: string,
    score: number,
    level: number,
    accuracy: number,
    isPersonalBest: boolean,
    reactionTimeMs?: number
  ) => Promise<boolean>;

  /** Track AWU session */
  trackAWUSession: (
    sessionType: string,
    durationMs: number,
    completedSteps?: number,
    totalSteps?: number
  ) => Promise<boolean>;
}

/**
 * Hook for tracking events in React components
 */
export function useEvents(options: UseEventsOptions = {}): UseEventsReturn {
  const hasTrackedPageView = useRef(false);

  // Track page view on mount if requested
  useEffect(() => {
    if (options.trackPageViewOnMount && !hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      const path = options.pagePath || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const title = options.pageTitle || (typeof document !== 'undefined' ? document.title : undefined);
      trackPageView(path, title);
    }
  }, [options.trackPageViewOnMount, options.pagePath, options.pageTitle]);

  // Generic track function
  const track = useCallback((input: EventInput): Promise<boolean> => {
    return trackEvent(input);
  }, []);

  // Track page view
  const trackPage = useCallback(
    (path?: string, title?: string): Promise<boolean> => {
      const p = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const t = title || (typeof document !== 'undefined' ? document.title : undefined);
      return trackPageView(p, t);
    },
    []
  );

  // Track mood check-in
  const trackMood = useCallback(
    (mood: 'stressed' | 'okay' | 'good', context?: string): Promise<boolean> => {
      return trackMoodCheckIn(mood, context);
    },
    []
  );

  // Track technique session
  const trackTechnique = useCallback(
    (
      techniqueId: string,
      techniqueName: string,
      durationMs: number,
      stressBefore?: number,
      stressAfter?: number
    ): Promise<boolean> => {
      return trackTechniqueSession(
        techniqueId,
        techniqueName,
        durationMs,
        stressBefore,
        stressAfter
      );
    },
    []
  );

  // Track module completion
  const trackModule = useCallback(
    (moduleId: string, moduleName: string, category?: string): Promise<boolean> => {
      return trackModuleCompleted(moduleId, moduleName, category);
    },
    []
  );

  // Track crisis detection
  const trackCrisis = useCallback(
    (
      method: 'deterministic' | 'ai_secondary',
      severity: 'low' | 'medium' | 'high',
      source: 'chat' | 'journal' | 'reflection' | 'feedback' | 'other'
    ): Promise<boolean> => {
      return trackCrisisDetected(method, severity, source);
    },
    []
  );

  // Track AI message
  const trackAIMessage = useCallback(
    (
      conversationId: string,
      messageIndex: number,
      direction: 'sent' | 'received'
    ): Promise<boolean> => {
      return trackAICoachMessage(conversationId, messageIndex, direction);
    },
    []
  );

  // Track onboarding step
  const trackOnboardingStep = useCallback(
    (stepId: string, stepName: string, stepIndex: number): Promise<boolean> => {
      return trackEvent({
        event_name: 'onboarding_step_completed',
        payload: {
          step_id: stepId,
          step_name: stepName,
          step_index: stepIndex,
        },
      });
    },
    []
  );

  // Track onboarding completion
  const trackOnboardingComplete = useCallback(
    (userIntent?: string, gradeLevel?: string, totalTimeMs?: number): Promise<boolean> => {
      return trackEvent({
        event_name: 'onboarding_completed',
        payload: {
          user_intent: userIntent,
          grade_level: gradeLevel,
          total_time_ms: totalTimeMs,
        },
      });
    },
    []
  );

  // Track goal creation
  const trackGoalCreated = useCallback(
    (
      goalId: string,
      goalType: 'academic' | 'skill' | 'wellness' | 'social' | 'personal',
      milestoneCount?: number
    ): Promise<boolean> => {
      return trackEvent({
        event_name: 'goal_created',
        payload: {
          goal_id: goalId,
          goal_type: goalType,
          milestone_count: milestoneCount,
        },
      });
    },
    []
  );

  // Track goal completion
  const trackGoalCompleted = useCallback((goalId: string): Promise<boolean> => {
    return trackEvent({
      event_name: 'goal_completed',
      payload: {
        goal_id: goalId,
      },
    });
  }, []);

  // Track journal entry
  const trackJournalEntry = useCallback(
    (
      entryType: 'personal' | 'reflection' | 'vent',
      wordCount: number,
      hasPrompt = false
    ): Promise<boolean> => {
      return trackEvent({
        event_name: 'journal_entry_created',
        payload: {
          entry_type: entryType,
          word_count: wordCount,
          has_prompt: hasPrompt,
        },
      });
    },
    []
  );

  // Track EF game
  const trackEFGame = useCallback(
    (
      gameId: string,
      gameName: string,
      score: number,
      level: number,
      accuracy: number,
      isPersonalBest: boolean,
      reactionTimeMs?: number
    ): Promise<boolean> => {
      return trackEvent({
        event_name: 'ef_game_completed',
        payload: {
          game_id: gameId,
          game_name: gameName,
          score,
          level,
          accuracy,
          is_personal_best: isPersonalBest,
          reaction_time_ms: reactionTimeMs,
        },
      });
    },
    []
  );

  // Track AWU session
  const trackAWUSession = useCallback(
    (
      sessionType: string,
      durationMs: number,
      completedSteps?: number,
      totalSteps?: number
    ): Promise<boolean> => {
      return trackEvent({
        event_name: 'awu_session_completed',
        payload: {
          session_type: sessionType,
          duration_ms: durationMs,
          completed_steps: completedSteps,
          total_steps: totalSteps,
        },
      });
    },
    []
  );

  return {
    track,
    trackPage,
    trackMood,
    trackTechnique,
    trackModule,
    trackCrisis,
    trackAIMessage,
    trackOnboardingStep,
    trackOnboardingComplete,
    trackGoalCreated,
    trackGoalCompleted,
    trackJournalEntry,
    trackEFGame,
    trackAWUSession,
  };
}
